// Client-side image compressor for POD photos.
//
// Camera-fresh photos on modern phones are 4–8 MB JPEGs at 4032×3024 or
// larger — way more resolution than we need for a delivery proof photo
// (~1024×1024 is plenty for reading addresses, packages, doors, etc.).
//
// We downsize via an offscreen canvas and re-encode to JPEG at q=0.82.
// Typical result: 4–8 MB → 150–350 KB (≈95% smaller) with no perceptible
// quality loss on a phone screen.
//
// EXIF orientation is preserved via the browser's `createImageBitmap`
// `imageOrientation: "from-image"` option (well supported on iOS 14+,
// Android 90+, desktop Chrome / Safari / Firefox). If unsupported, the
// helper falls back to drawing the raw image — orientation may be off
// but the upload still succeeds.

// Tuned 2026 for POD upload speed on weak LTE: dropping from 1024/0.82 to
// 800/0.78 roughly halves the per-photo payload (150–350 KB → 80–180 KB)
// with no perceptible loss on prescription labels or door numbers on a
// phone screen. Applies to both POD and Failed-delivery photos.
const MAX_DIM = 800;
const QUALITY = 0.78;

export async function compressImage(file, options = {}) {
  const maxDim = options.maxDim || MAX_DIM;
  const quality = options.quality || QUALITY;
// Also adjust the "skip if already small" floor: with 800px/q=0.78 the
// typical output is ~120 KB, so files under 120 KB are already at or
// below what we would produce — no point re-encoding them.
  const skipBelowBytes = options.skipBelowBytes ?? 120 * 1024;
  // Skip non-image files (rare with capture=environment, but safe).
  if (!file || !file.type || !file.type.startsWith("image/")) return file;
  // Already small? Skip the round-trip.
  if (file.size < skipBelowBytes) return file;

  let bitmap;
  try {
    bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  } catch {
    // Older Safari / Android without imageOrientation support.
    try {
      bitmap = await createImageBitmap(file);
    } catch {
      // createImageBitmap unavailable at all → bail out, upload original.
      return file;
    }
  }

  const { width: w, height: h } = bitmap;
  const scale = Math.min(1, maxDim / Math.max(w, h));
  const tw = Math.round(w * scale);
  const th = Math.round(h * scale);

  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(bitmap, 0, 0, tw, th);
  bitmap.close?.();

  // PNG output (for logos with transparency) or JPEG (for photos).
  const outputType = options.outputType || "image/jpeg";
  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, outputType, outputType === "image/jpeg" ? quality : undefined)
  );
  if (!blob) return file;

  // If the "compressed" version is somehow larger (tiny camera, etc.), keep original.
  if (blob.size >= file.size) return file;

  // Rebuild as a File so FormData treats it like a normal upload.
  const ext = outputType === "image/png" ? ".png" : ".jpg";
  const renamed = (file.name || `image${ext}`).replace(/\.\w+$/, ext);
  return new File([blob], renamed, { type: outputType, lastModified: Date.now() });
}

/**
 * Pharmacy logo helper. Logos are typically wide rectangular images that
 * get rendered at ~300px wide in the label PDF / table thumbnails. We allow
 * the source to be up to 600px on the longest side and keep PNG so any
 * transparent background is preserved. Falls through to no-op on files
 * already under 200 KB.
 */
export function compressLogo(file) {
  // Heuristic: if it's already a small PNG (likely an icon), keep it.
  if (file && file.size < 200 * 1024) return Promise.resolve(file);
  // PNG sources keep alpha; JPEG sources keep JPEG.
  const outputType = (file && file.type === "image/png") ? "image/png" : "image/jpeg";
  return compressImage(file, { maxDim: 600, quality: 0.9, skipBelowBytes: 200 * 1024, outputType });
}
